import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Interweave from 'interweave';
import { createTheme } from '@material-ui/core/styles';
import { Accordion, AccordionDetails, AccordionSummary, ThemeProvider } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Color } from '@sunrun/mui-theme';
import mixpanel from '../../utilities/mixpanel';
import { MIN_DESKTOP_BREAKPOINT } from '../general/constants/_breakpoints.js';
import { HEADER_HEIGHT } from '../general/constants/_sizes.js';
import { DARK_BLUE_COLOR, SECONDARY_COLOR } from '../general/constants/_colors.js';
import UserFeedbackComponent from './UserFeedbackComponent.jsx';

const Title = styled.div`
  cursor: pointer;

  p {
    display: inline-block;
    margin: 0;
    color: ${Color.BLACK_85};
    font-size: ${props => props.titleFontSize};
    font-family: Roobert, sans-serif;
  }
`;

const Answer = styled.div`
  > span {
    display: block;
    margin-left: 16px;

    a {
      color: ${SECONDARY_COLOR};
    }

    li {
      margin: 8px auto;

      a {
        color: ${SECONDARY_COLOR};
      }
    }

    @media only screen and (min-width: ${MIN_DESKTOP_BREAKPOINT}) {
      margin-left: 35px;
    }
  }
`;

const tableTheme = createTheme({
  overrides: {
    MuiAccordion: {
      root: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '&::before': {
          height: '0px',
        },
        '&$expanded': {
          margin: '0px',
        },
      },
    },
    MuiAccordionSummary: {
      root: {
        padding: '0px 30px',
        '&$expanded': {
          fontWeight: 'bold',
        },
      },
      expandIcon: {
        position: 'absolute',
        left: '-10px',
        color: DARK_BLUE_COLOR,
      },
      content: {
        '&$expanded': {
          margin: '12px 0;',
        },
      },
    },
    MuiAccordionDetails: {
      root: {
        padding: '0 16px 10px',
      },
    },
  },
});

const QuestionSectionComponent = ({ questions, onQuestionSelect, viewedQMixpanelMessage, leftFeedbackMixpanelMessage, titleFontSize, scrollContainerRef }) => {
  const [expanded, setExpanded] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    setExpanded(hash.replace('#', ''));
  }, [hash]);

  const handleSetExpanded = question => (event, isExpanded) => {
    onQuestionSelect(question);
    setExpanded(isExpanded ? question.link : false);
  };

  const scrollToQuestion = () => {
    if (expanded) {
      const container = scrollContainerRef.current;

      const element = document.querySelector(`#${expanded}`);
      if (container && element) {
        const viewportOffset = element.getBoundingClientRect();
        const toScroll = viewportOffset.top - parseInt(HEADER_HEIGHT);
        const top = container.scrollTop + toScroll;
        container.scrollTo({
          top,
          left: 0,
          behavior: 'smooth',
        });
      }
    }
  };

  const onExited = () => {
    scrollToQuestion();
  };

  const onMounted = () => {
    scrollToQuestion();
  };

  const handleTransformHTML = node => {
    if (node.tagName === 'IFRAME') {
      return <iframe width="75%" height="300px" title={node.title} src={node.src} />;
    }
    // returning undefined allows the default component to be used: https://interweave.dev/docs/parser/#replacing-elements
    return undefined;
  };

  return (
    <ThemeProvider theme={tableTheme}>
      {questions.map(question => {
        if (!question.title || !question.content) return null;
        return (
          <Accordion
            key={`faq-content-${question.link}`}
            square
            onChange={handleSetExpanded(question)}
            expanded={expanded === question.link}
            id={question.link}
            TransitionProps={{ onExited }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${question.link}-content`}
              id={`${question.link}-header`}
              onClick={() => {
                mixpanel.track(viewedQMixpanelMessage, {
                  Question: question.title,
                });
              }}
            >
              <Title titleFontSize={titleFontSize}>
                <p>{question.title}</p>
              </Title>
            </AccordionSummary>
            <AccordionDetails>
              <Answer>
                <Interweave transform={handleTransformHTML} content={question.content} />
                <UserFeedbackComponent question={question} mixpanelMessage={leftFeedbackMixpanelMessage} />
              </Answer>
            </AccordionDetails>
            <Mounted onMounted={onMounted} />
          </Accordion>
        );
      })}
    </ThemeProvider>
  );
};

const Mounted = ({ onMounted }) => {
  useEffect(() => {
    onMounted();
  }, []);
  return null;
};

QuestionSectionComponent.propTypes = {
  questions: PropTypes.array.isRequired,
  viewedQMixpanelMessage: PropTypes.string.isRequired,
  leftFeedbackMixpanelMessage: PropTypes.string.isRequired,
  titleFontSize: PropTypes.string,
  onQuestionSelect: PropTypes.func.isRequired,
  scrollContainerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]).isRequired,
};

QuestionSectionComponent.defaultProps = {
  titleFontSize: '18px',
};

export default QuestionSectionComponent;
